import { observer } from 'mobx-react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { Illustration } from '@/components/Illustration/Illustration'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, AGE_VERIFICATION_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { getUrlParam } from '@/methods/urlParams'
import { cx } from '@/utils/cx'
import {
  isWebAuthnSupported,
  parseCreationOptions,
  PublicKeyCredentialCreationOptionsJSON,
  registrationResponseToJSON,
} from '@/methods/webauthn'
import './AgeVerification.css'

interface CreateEidTransactionResponse {
  proceedUrl: string
}

export const AgeVerificationRegistrationPage = observer(() => {
  const buttonRef = useRef(null)

  const store = useContext(StoreContext)
  const {
    ageVerificationPage: { registration: trans, eidReturn: eidReturnTrans },
  } = store.TranslationsState.translations

  const [btnLoading, setBtnLoading] = useState(false)
  const [retryBtnLoading, setRetryBtnLoading] = useState(false)
  const [error, setError] = useState('')

  // Set by the BE redirect after the eID flow: completed | canceled | failed
  const eidStatus = getUrlParam('status')
  const isEidCanceled = eidStatus === 'canceled'
  // The BE renamed 'failure' to 'failed'; keep matching both to be safe
  const isEidFailed = eidStatus === 'failed' || eidStatus === 'failure'
  // Which verification the status belongs to: the eID provider (default) or PictureID
  const failedMethodPath = getUrlParam('method') === 'pictureId' ? 'picture-id' : 'eid'

  const handleSkip = () => {
    store.ScenarioState.skipStep()
  }

  const handleRegister = async () => {
    setBtnLoading(true)
    setError('')

    try {
      const { data: creationOptions } =
        await axiosInstance.post<PublicKeyCredentialCreationOptionsJSON>(
          `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/registration/start`,
          {},
          {
            withCredentials: true,
          }
        )

      const credential = (await navigator.credentials.create({
        publicKey: parseCreationOptions(creationOptions),
      })) as PublicKeyCredential | null

      if (!credential) {
        throw new Error('No credential returned')
      }

      // Responds with 204 on success and throws (4xx/5xx) on verification failure
      await axiosInstance.post(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/registration/verify`,
        registrationResponseToJSON(credential),
        {
          withCredentials: true,
        }
      )

      store.ScenarioState.nextPage()
    } catch (e) {
      devLog('Passkey registration failed:', e)
      setError(trans.error)
      setBtnLoading(false)
    }
  }

  const handleRetry = async () => {
    setRetryBtnLoading(true)

    try {
      // Recreates the verification (eID or PictureID) so the user can retry it directly
      const { data } = await axiosInstance.post<CreateEidTransactionResponse>(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/${failedMethodPath}`,
        {},
        {
          withCredentials: true,
        }
      )

      data?.proceedUrl && forceRedirect(data.proceedUrl)
    } catch (e) {
      devLog('Error recreating the verification:', e)
      setRetryBtnLoading(false)
    }
  }

  const handleCancel = () => {
    // The BE completes the process and redirects the user to the transaction's redirect URL
    forceRedirect(`${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/complete`)
  }

  useEffect(() => {
    // Nothing to register on browsers without WebAuthn support
    if (!isEidCanceled && !isEidFailed && !isWebAuthnSupported()) {
      store.ScenarioState.skipStep()
    }
  }, [])

  if (isEidCanceled || isEidFailed) {
    return (
      <div className="page-container mobile-m-0 vertically-centered">
        <Illustration
          type={isEidCanceled ? 'transaction-cancelled' : 'identity-verification-failed'}
        />

        <Header className="m-0-0-30">
          {isEidCanceled ? eidReturnTrans.canceledHeader : eidReturnTrans.failureHeader}
        </Header>

        <p className="generic__paragraph-centered m-0-0-30">
          {isEidCanceled ? eidReturnTrans.canceledDescription : eidReturnTrans.failureDescription}
        </p>

        <Button
          onClick={handleRetry}
          tabIndex={0}
          className="button--fit-content-center"
          disabled={retryBtnLoading}
        >
          {retryBtnLoading && <LoadingSpinner inButton />}
          <span
            className={cx(
              'generic__button-text',
              retryBtnLoading && 'generic__button-text--loading'
            )}
            data-testId="retry"
          >
            {eidReturnTrans.retry}
          </span>
        </Button>

        <div className="generic__spacer h-15" />

        <Button
          onClick={handleCancel}
          tabIndex={0}
          className="button--secondary button--fit-content-center"
          disabled={retryBtnLoading}
        >
          <span className="generic__button-text" data-testId="cancel">
            {eidReturnTrans.cancel}
          </span>
        </Button>
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <div className="layout__content-container-with-scroll layout__content-container-with-scroll-centered age-verification__content--two-buttons">
        <Illustration type="passkey-lock" />

        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph-centered">{trans.description}</p>

        {error && (
          <div className="generic__onboarding-item">
            <div className="generic__onboarding-icon">
              <Icon type="warning" size="32px" color="var(--text-input-error-color, #941616)" />
            </div>
            <span className="generic__onboarding-single-line generic__error-message">{error}</span>
          </div>
        )}
      </div>

      <div className="layout__content-container-bottom-fixed-centered">
        <div className="layout__vertically-centered-container">
          <Button
            onClick={handleRegister}
            tabIndex={0}
            className="button--proceed"
            ref={buttonRef}
            disabled={btnLoading}
          >
            {btnLoading && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
              data-testId="proceed"
            >
              {trans.createPasskey}
            </span>
          </Button>

          <div className="generic__spacer h-15" />

          <Button onClick={handleSkip} tabIndex={0} className="button--secondary">
            <span className="generic__button-text" data-testId="skip">
              {trans.skip}
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
})
